"use client";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import type { IModuleUspBanner } from "@contentful-api/types/contentful";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { VisuallyHidden } from "@ui/components/other/visually-hidden/VisuallyHidden";
import { forwardRef } from "@ui/utils/theme-tools";
import { useScrollDirection } from "@hooks/useScrollDirection";
import { useMarquee } from "@hooks/useMarquee";
import { USPMessagesList } from "./components/USPMessagesList";
import {
    getWrapperStyle,
    containerStyle,
    marqueeTrackStyle,
    marqueeContentStyle,
    staticMessageStyle,
    rotatingMessageContainerStyle,
    getRotatingMessageStyle,
} from "./USPbanner.styles";
import { useUSPBannerConfig } from "./hooks/useUSPBannerConfig";

const BANNER_DEADZONE = 30;

export interface IUSPBannerProps {
    uspBanner: IModuleUspBanner;
    backgroundColor?: string;
}

export const USPbanner = forwardRef(({ uspBanner, backgroundColor }: IUSPBannerProps, ref) => {
    const { scrollDirection, isInDeadzone } = useScrollDirection({ deadZone: BANNER_DEADZONE });
    const isVisible = isInDeadzone || scrollDirection === "up";

    const { bannerBackground, animatedContainerStyle, uspMessages, isSaleSeason } =
        useUSPBannerConfig({
            uspBanner,
            backgroundColor,
        });

    const { containerRef, contentRef, shouldScroll, marqueeProps } = useMarquee({
        speed: 50,
        pauseOnHover: true,
        gap: 100,
        enabled: true,
    });
    const { style: marqueeStyle, ...marqueeRest } = marqueeProps;

    return (
        <Box sx={getWrapperStyle(isVisible)}>
            <Box ref={ref} w="100%" shadow="base" px={0}>
                <VisuallyHidden>
                    {uspMessages.map((item, i) => (
                        <Box key={`uspMessage${i}`}>
                            <RichText data={item} />
                        </Box>
                    ))}
                </VisuallyHidden>
                <Flex
                    sx={{
                        ...containerStyle,
                        height: "6",
                        justifyContent: "center",
                        background: bannerBackground,
                    }}
                    data-testid="USPBanner"
                >
                    <Box ref={containerRef} sx={animatedContainerStyle} aria-hidden="true">
                        {/* Mobile: Marquee scrolling or static message */}
                        <Box sx={marqueeTrackStyle}>
                            {shouldScroll ? (
                                <Box
                                    {...marqueeRest}
                                    sx={{
                                        ...marqueeContentStyle,
                                        ...marqueeStyle,
                                    }}
                                >
                                    {/* First copy */}
                                    <Box
                                        ref={contentRef}
                                        display="inline-flex"
                                        alignItems="center"
                                        mr={10}
                                    >
                                        <USPMessagesList
                                            messages={uspMessages}
                                            isSaleSeason={isSaleSeason}
                                            showSeparator={uspMessages.length > 1}
                                        />
                                    </Box>
                                    {/* Second copy for seamless loop */}
                                    <Box
                                        display="inline-flex"
                                        alignItems="center"
                                        aria-hidden="true"
                                        mr={10}
                                    >
                                        <USPMessagesList
                                            messages={uspMessages}
                                            keyPrefix="dup-"
                                            isSaleSeason={isSaleSeason}
                                            showSeparator={uspMessages.length > 1}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Box ref={contentRef} sx={staticMessageStyle}>
                                    <USPMessagesList
                                        messages={uspMessages}
                                        isSaleSeason={isSaleSeason}
                                        showSeparator={uspMessages.length > 1}
                                    />
                                </Box>
                            )}
                        </Box>
                        {/* Desktop: Rotating messages */}
                        <Flex sx={rotatingMessageContainerStyle}>
                            {uspMessages.map((message, index) => (
                                <Box
                                    key={index}
                                    sx={getRotatingMessageStyle(index, uspMessages.length)}
                                >
                                    <RichText data={message} />
                                </Box>
                            ))}
                        </Flex>
                    </Box>
                </Flex>
            </Box>
        </Box>
    );
});

export default USPbanner;
